import "./CompanySuppliers.css";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "/api";

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const emptySupplier = {
  nit: "",
  name: "",
  contact_name: "",
  email: "",
  phone: "",
  address: "",
  notes: "",
  is_active: true,
};

const emptyContract = {
  title: "",
  description: "",
  start_date: "",
  end_date: "",
  amount: "",
  document_url: "",
  is_active: true,
};

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const emptyEvaluation = {
  year: currentYear,
  month: currentMonth,
  quality_score: 5,
  delivery_score: 5,
  compliance_score: 5,
  observations: "",
};

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible completar la operación.";
};

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "";

  return new Date(`${value}T00:00:00`).toLocaleDateString(
    "es-CO"
  );
};

const getMonthName = (month) => {
  const months = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  return months[Number(month) - 1] || "";
};

function CompanySuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [savingSupplier, setSavingSupplier] = useState(false);

  const [search, setSearch] = useState("");
  const [supplierForm, setSupplierForm] = useState(emptySupplier);
  const [editingSupplier, setEditingSupplier] = useState(null);

  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [contracts, setContracts] = useState([]);
  const [contractForm, setContractForm] = useState(emptyContract);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [savingContract, setSavingContract] = useState(false);

  const [evaluations, setEvaluations] = useState([]);
  const [evaluationForm, setEvaluationForm] = useState(emptyEvaluation);
  const [loadingEvaluations, setLoadingEvaluations] = useState(false);
  const [savingEvaluation, setSavingEvaluation] = useState(false);

  const [showContractsModal, setShowContractsModal] = useState(false);
  const [showEvaluationsModal, setShowEvaluationsModal] = useState(false);

  const loadSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/suppliers/me`, {
        headers: getHeaders(),
      });

      setSuppliers(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, []);

  const loadPerformance = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/suppliers/me/performance/summary`,
        {
          headers: getHeaders(),
        }
      );

      setPerformance(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadExpiringContracts = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/suppliers/me/contracts/expiring`,
        {
          headers: getHeaders(),
        }
      );

      setExpiringContracts(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadInitialData = useCallback(async () => {
    setLoading(true);

    await Promise.all([
      loadSuppliers(),
      loadPerformance(),
      loadExpiringContracts(),
    ]);

    setLoading(false);
  }, [
    loadSuppliers,
    loadPerformance,
    loadExpiringContracts,
  ]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleSupplierChange = (event) => {
    const { name, value, type, checked } = event.target;

    setSupplierForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetSupplierForm = () => {
    setSupplierForm(emptySupplier);
    setEditingSupplier(null);
  };

  const handleSupplierSubmit = async (event) => {
    event.preventDefault();

    if (!supplierForm.nit.trim() || !supplierForm.name.trim()) {
      toast.error("El NIT y el nombre del proveedor son obligatorios.");
      return;
    }

    setSavingSupplier(true);

    try {
      const payload = {
        ...supplierForm,
        nit: supplierForm.nit.trim(),
        name: supplierForm.name.trim(),
      };

      if (editingSupplier) {
        await axios.put(
          `${API_URL}/suppliers/me/${editingSupplier.id}`,
          payload,
          { headers: getHeaders() }
        );

        toast.success("Proveedor actualizado correctamente.");
      } else {
        await axios.post(
          `${API_URL}/suppliers/me`,
          payload,
          { headers: getHeaders() }
        );

        toast.success("Proveedor creado correctamente.");
      }

      resetSupplierForm();
      await loadInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingSupplier(false);
    }
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);

    setSupplierForm({
      nit: supplier.nit || "",
      name: supplier.name || "",
      contact_name: supplier.contact_name || "",
      email: supplier.email || "",
      phone: supplier.phone || "",
      address: supplier.address || "",
      notes: supplier.notes || "",
      is_active: Boolean(supplier.is_active),
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleToggleSupplier = async (supplier) => {
    try {
      await axios.patch(
        `${API_URL}/suppliers/me/${supplier.id}/toggle-active`,
        {},
        { headers: getHeaders() }
      );

      toast.success(
        supplier.is_active
          ? "Proveedor inactivado."
          : "Proveedor activado."
      );

      await loadInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const loadContracts = async (supplierId) => {
    setLoadingContracts(true);

    try {
      const response = await axios.get(
        `${API_URL}/suppliers/me/${supplierId}/contracts`,
        {
          headers: getHeaders(),
        }
      );

      setContracts(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingContracts(false);
    }
  };

  const openContractsModal = async (supplier) => {
    setSelectedSupplier(supplier);
    setContractForm(emptyContract);
    setShowContractsModal(true);
    await loadContracts(supplier.id);
  };

  const closeContractsModal = () => {
    setShowContractsModal(false);
    setSelectedSupplier(null);
    setContracts([]);
    setContractForm(emptyContract);
  };

  const handleContractChange = (event) => {
    const { name, value, type, checked } = event.target;

    setContractForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleContractSubmit = async (event) => {
    event.preventDefault();

    if (
      !contractForm.title.trim() ||
      !contractForm.start_date ||
      !contractForm.end_date
    ) {
      toast.error(
        "Título, fecha inicial y fecha final son obligatorios."
      );
      return;
    }

    if (contractForm.end_date < contractForm.start_date) {
      toast.error(
        "La fecha final no puede ser menor que la fecha inicial."
      );
      return;
    }

    setSavingContract(true);

    try {
      await axios.post(
        `${API_URL}/suppliers/me/${selectedSupplier.id}/contracts`,
        {
          ...contractForm,
          title: contractForm.title.trim(),
          amount: Number(contractForm.amount || 0),
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success("Contrato registrado correctamente.");
      setContractForm(emptyContract);

      await loadContracts(selectedSupplier.id);
      await loadInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingContract(false);
    }
  };

  const handleToggleContract = async (contract) => {
    try {
      await axios.patch(
        `${API_URL}/suppliers/me/${selectedSupplier.id}/contracts/${contract.id}/toggle-active`,
        {},
        {
          headers: getHeaders(),
        }
      );

      toast.success(
        contract.is_active
          ? "Contrato inactivado."
          : "Contrato activado."
      );

      await loadContracts(selectedSupplier.id);
      await loadInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const loadEvaluations = async (supplierId) => {
    setLoadingEvaluations(true);

    try {
      const response = await axios.get(
        `${API_URL}/suppliers/me/${supplierId}/evaluations`,
        {
          headers: getHeaders(),
        }
      );

      setEvaluations(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingEvaluations(false);
    }
  };

  const openEvaluationsModal = async (supplier) => {
    setSelectedSupplier(supplier);
    setEvaluationForm(emptyEvaluation);
    setShowEvaluationsModal(true);
    await loadEvaluations(supplier.id);
  };

  const closeEvaluationsModal = () => {
    setShowEvaluationsModal(false);
    setSelectedSupplier(null);
    setEvaluations([]);
    setEvaluationForm(emptyEvaluation);
  };

  const handleEvaluationChange = (event) => {
    const { name, value } = event.target;

    setEvaluationForm((current) => ({
      ...current,
      [name]:
        name === "observations"
          ? value
          : Number(value),
    }));
  };

  const handleEvaluationSubmit = async (event) => {
    event.preventDefault();

    setSavingEvaluation(true);

    try {
      await axios.post(
        `${API_URL}/suppliers/me/${selectedSupplier.id}/evaluations`,
        evaluationForm,
        {
          headers: getHeaders(),
        }
      );

      toast.success("Evaluación mensual registrada correctamente.");
      setEvaluationForm(emptyEvaluation);

      await loadEvaluations(selectedSupplier.id);
      await loadInitialData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingEvaluation(false);
    }
  };

  const filteredSuppliers = suppliers.filter((supplier) => {
    const term = search.trim().toLowerCase();

    if (!term) return true;

    return (
      supplier.name?.toLowerCase().includes(term) ||
      supplier.nit?.toLowerCase().includes(term) ||
      supplier.contact_name?.toLowerCase().includes(term)
    );
  });

  const activeSuppliers = suppliers.filter(
    (supplier) => supplier.is_active
  ).length;

  const averageScore =
    performance.length > 0
      ? performance.reduce(
          (total, item) => total + Number(item.average_score || 0),
          0
        ) / performance.length
      : 0;

  return (
    <div className="company-suppliers">
      <section className="suppliers-heading">
        <div>
          <p className="suppliers-kicker">Compras</p>
          <h1>Proveedores</h1>
          <p>
            Gestiona proveedores, contratos y desempeño mensual.
          </p>
        </div>

        <button
          type="button"
          className="btn-suppliers-refresh"
          onClick={loadInitialData}
          disabled={loading}
        >
          {loading ? "Actualizando..." : "Actualizar"}
        </button>
      </section>

      <section className="suppliers-stats">
        <article className="supplier-stat-card">
          <span>Proveedores registrados</span>
          <strong>{suppliers.length}</strong>
        </article>

        <article className="supplier-stat-card success">
          <span>Proveedores activos</span>
          <strong>{activeSuppliers}</strong>
        </article>

        <article className="supplier-stat-card warning">
          <span>Contratos por vencer</span>
          <strong>{expiringContracts.length}</strong>
        </article>

        <article className="supplier-stat-card">
          <span>Desempeño promedio</span>
          <strong>{averageScore.toFixed(2)} / 5</strong>
        </article>
      </section>

      {expiringContracts.length > 0 && (
        <section className="supplier-alert">
          <strong>
            Hay {expiringContracts.length} contrato(s) próximo(s) a vencer.
          </strong>

          <span>
            Revísalos desde el botón “Contratos” de cada proveedor.
          </span>
        </section>
      )}

      <section className="suppliers-card supplier-form-card">
        <div className="supplier-card-heading">
          <div>
            <h2>
              {editingSupplier
                ? "Editar proveedor"
                : "Registrar proveedor"}
            </h2>

            <p>
              Los campos NIT y nombre son obligatorios.
            </p>
          </div>

          {editingSupplier && (
            <button
              type="button"
              className="btn-supplier-cancel"
              onClick={resetSupplierForm}
            >
              Cancelar edición
            </button>
          )}
        </div>

        <form
          className="supplier-form"
          onSubmit={handleSupplierSubmit}
        >
          <div className="supplier-form-grid">
            <label>
              NIT
              <input
                type="text"
                name="nit"
                value={supplierForm.nit}
                onChange={handleSupplierChange}
                placeholder="Ejemplo: 900123456-7"
                required
              />
            </label>

            <label>
              Nombre o razón social
              <input
                type="text"
                name="name"
                value={supplierForm.name}
                onChange={handleSupplierChange}
                placeholder="Nombre del proveedor"
                required
              />
            </label>

            <label>
              Persona de contacto
              <input
                type="text"
                name="contact_name"
                value={supplierForm.contact_name}
                onChange={handleSupplierChange}
                placeholder="Nombre del contacto"
              />
            </label>

            <label>
              Correo electrónico
              <input
                type="email"
                name="email"
                value={supplierForm.email}
                onChange={handleSupplierChange}
                placeholder="correo@empresa.com"
              />
            </label>

            <label>
              Teléfono
              <input
                type="text"
                name="phone"
                value={supplierForm.phone}
                onChange={handleSupplierChange}
                placeholder="Número telefónico"
              />
            </label>

            <label>
              Dirección
              <input
                type="text"
                name="address"
                value={supplierForm.address}
                onChange={handleSupplierChange}
                placeholder="Dirección comercial"
              />
            </label>
          </div>

          <label className="supplier-notes">
            Observaciones
            <textarea
              name="notes"
              value={supplierForm.notes}
              onChange={handleSupplierChange}
              placeholder="Información adicional del proveedor"
              rows="3"
            />
          </label>

          <label className="supplier-check">
            <input
              type="checkbox"
              name="is_active"
              checked={supplierForm.is_active}
              onChange={handleSupplierChange}
            />
            Proveedor activo
          </label>

          <button
            type="submit"
            className="btn-supplier-save"
            disabled={savingSupplier}
          >
            {savingSupplier
              ? "Guardando..."
              : editingSupplier
                ? "Actualizar proveedor"
                : "Crear proveedor"}
          </button>
        </form>
      </section>

      <section className="suppliers-card supplier-list-card">
        <div className="supplier-card-heading supplier-list-heading">
          <div>
            <h2>Listado de proveedores</h2>
            <p>Administra la información, contratos y desempeño.</p>
          </div>

          <input
            className="supplier-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por proveedor, NIT o contacto"
          />
        </div>

        <div className="supplier-table-wrapper">
          <table className="supplier-table">
            <thead>
              <tr>
                <th>Proveedor</th>
                <th>NIT</th>
                <th>Contacto</th>
                <th>Teléfono</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="supplier-empty">
                    Cargando proveedores...
                  </td>
                </tr>
              ) : filteredSuppliers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="supplier-empty">
                    No hay proveedores para mostrar.
                  </td>
                </tr>
              ) : (
                filteredSuppliers.map((supplier) => (
                  <tr key={supplier.id}>
                    <td>
                      <strong>{supplier.name}</strong>
                      {supplier.email && (
                        <small>{supplier.email}</small>
                      )}
                    </td>

                    <td>{supplier.nit}</td>

                    <td>
                      {supplier.contact_name || "Sin contacto"}
                    </td>

                    <td>{supplier.phone || "Sin teléfono"}</td>

                    <td>
                      <span
                        className={
                          supplier.is_active
                            ? "supplier-status active"
                            : "supplier-status inactive"
                        }
                      >
                        {supplier.is_active ? "Activo" : "Inactivo"}
                      </span>
                    </td>

                    <td>
                      <div className="supplier-actions">
                        <button
                          type="button"
                          className="btn-supplier-edit"
                          onClick={() => handleEditSupplier(supplier)}
                        >
                          Editar
                        </button>

                        <button
                          type="button"
                          className="btn-supplier-contract"
                          onClick={() => openContractsModal(supplier)}
                        >
                          Contratos
                        </button>

                        <button
                          type="button"
                          className="btn-supplier-evaluation"
                          onClick={() =>
                            openEvaluationsModal(supplier)
                          }
                        >
                          Evaluar
                        </button>

                        <button
                          type="button"
                          className={
                            supplier.is_active
                              ? "btn-supplier-disable"
                              : "btn-supplier-enable"
                          }
                          onClick={() =>
                            handleToggleSupplier(supplier)
                          }
                        >
                          {supplier.is_active
                            ? "Inactivar"
                            : "Activar"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="suppliers-card supplier-performance-card">
        <div className="supplier-card-heading">
          <div>
            <h2>Desempeño de proveedores</h2>
            <p>
              Promedio de las evaluaciones mensuales registradas.
            </p>
          </div>
        </div>

        <div className="supplier-table-wrapper">
          <table className="supplier-table performance-table">
            <thead>
              <tr>
                <th>Proveedor</th>
                <th>Evaluaciones</th>
                <th>Calidad</th>
                <th>Entrega</th>
                <th>Cumplimiento</th>
                <th>Promedio</th>
                <th>Contratos</th>
              </tr>
            </thead>

            <tbody>
              {performance.length === 0 ? (
                <tr>
                  <td colSpan="7" className="supplier-empty">
                    Aún no existen evaluaciones registradas.
                  </td>
                </tr>
              ) : (
                performance.map((item) => (
                  <tr key={item.supplier_id}>
                    <td>
                      <strong>{item.supplier_name}</strong>
                      <small>{item.supplier_nit}</small>
                    </td>

                    <td>{item.evaluations_count}</td>
                    <td>{Number(item.average_quality || 0).toFixed(2)}</td>
                    <td>{Number(item.average_delivery || 0).toFixed(2)}</td>
                    <td>
                      {Number(item.average_compliance || 0).toFixed(2)}
                    </td>

                    <td>
                      <span
                        className={
                          Number(item.average_score || 0) >= 4
                            ? "supplier-score good"
                            : Number(item.average_score || 0) >= 3
                              ? "supplier-score regular"
                              : "supplier-score low"
                        }
                      >
                        {Number(item.average_score || 0).toFixed(2)} / 5
                      </span>
                    </td>

                    <td>
                      {item.active_contracts} activos
                      {item.expiring_contracts > 0 && (
                        <small className="contract-warning">
                          {item.expiring_contracts} por vencer
                        </small>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {showContractsModal && selectedSupplier && (
        <div className="supplier-modal-backdrop">
          <section className="supplier-modal">
            <div className="supplier-modal-header">
              <div>
                <p className="suppliers-kicker">Contratos</p>
                <h2>{selectedSupplier.name}</h2>
              </div>

              <button
                type="button"
                className="supplier-modal-close"
                onClick={closeContractsModal}
              >
                ×
              </button>
            </div>

            <form
              className="supplier-contract-form"
              onSubmit={handleContractSubmit}
            >
              <h3>Registrar contrato</h3>

              <div className="supplier-form-grid">
                <label>
                  Título
                  <input
                    type="text"
                    name="title"
                    value={contractForm.title}
                    onChange={handleContractChange}
                    placeholder="Ejemplo: Suministro de granos"
                    required
                  />
                </label>

                <label>
                  Monto del contrato
                  <input
                    type="number"
                    min="0"
                    name="amount"
                    value={contractForm.amount}
                    onChange={handleContractChange}
                    placeholder="0"
                  />
                </label>

                <label>
                  Fecha de inicio
                  <input
                    type="date"
                    name="start_date"
                    value={contractForm.start_date}
                    onChange={handleContractChange}
                    required
                  />
                </label>

                <label>
                  Fecha de vencimiento
                  <input
                    type="date"
                    name="end_date"
                    value={contractForm.end_date}
                    onChange={handleContractChange}
                    required
                  />
                </label>
              </div>

              <label className="supplier-notes">
                Descripción
                <textarea
                  name="description"
                  value={contractForm.description}
                  onChange={handleContractChange}
                  placeholder="Condiciones o alcance del contrato"
                  rows="2"
                />
              </label>

              <label className="supplier-notes">
                Enlace del documento
                <input
                  type="url"
                  name="document_url"
                  value={contractForm.document_url}
                  onChange={handleContractChange}
                  placeholder="https://..."
                />
              </label>

              <button
                type="submit"
                className="btn-supplier-save"
                disabled={savingContract}
              >
                {savingContract
                  ? "Guardando..."
                  : "Guardar contrato"}
              </button>
            </form>

            <div className="supplier-modal-list">
              <h3>Contratos registrados</h3>

              {loadingContracts ? (
                <p className="supplier-empty">Cargando contratos...</p>
              ) : contracts.length === 0 ? (
                <p className="supplier-empty">
                  Este proveedor no tiene contratos registrados.
                </p>
              ) : (
                contracts.map((contract) => (
                  <article
                    className="supplier-contract-item"
                    key={contract.id}
                  >
                    <div>
                      <strong>{contract.title}</strong>
                      <span>
                        {formatDate(contract.start_date)} —{" "}
                        {formatDate(contract.end_date)}
                      </span>
                      <small>{money(contract.amount)}</small>
                    </div>

                    <div className="supplier-contract-actions">
                      {contract.document_url && (
                        <a
                          href={contract.document_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Documento
                        </a>
                      )}

                      <button
                        type="button"
                        onClick={() => handleToggleContract(contract)}
                      >
                        {contract.is_active
                          ? "Inactivar"
                          : "Activar"}
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>
        </div>
      )}

      {showEvaluationsModal && selectedSupplier && (
        <div className="supplier-modal-backdrop">
          <section className="supplier-modal">
            <div className="supplier-modal-header">
              <div>
                <p className="suppliers-kicker">Evaluación mensual</p>
                <h2>{selectedSupplier.name}</h2>
              </div>

              <button
                type="button"
                className="supplier-modal-close"
                onClick={closeEvaluationsModal}
              >
                ×
              </button>
            </div>

            <form
              className="supplier-evaluation-form"
              onSubmit={handleEvaluationSubmit}
            >
              <h3>Registrar evaluación</h3>

              <div className="supplier-form-grid">
                <label>
                  Año
                  <input
                    type="number"
                    name="year"
                    min="2020"
                    max="2100"
                    value={evaluationForm.year}
                    onChange={handleEvaluationChange}
                    required
                  />
                </label>

                <label>
                  Mes
                  <select
                    name="month"
                    value={evaluationForm.month}
                    onChange={handleEvaluationChange}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                      (month) => (
                        <option key={month} value={month}>
                          {getMonthName(month)}
                        </option>
                      )
                    )}
                  </select>
                </label>

                <label>
                  Calidad (0 a 5)
                  <input
                    type="number"
                    name="quality_score"
                    min="0"
                    max="5"
                    step="0.1"
                    value={evaluationForm.quality_score}
                    onChange={handleEvaluationChange}
                    required
                  />
                </label>

                <label>
                  Entrega (0 a 5)
                  <input
                    type="number"
                    name="delivery_score"
                    min="0"
                    max="5"
                    step="0.1"
                    value={evaluationForm.delivery_score}
                    onChange={handleEvaluationChange}
                    required
                  />
                </label>

                <label>
                  Cumplimiento (0 a 5)
                  <input
                    type="number"
                    name="compliance_score"
                    min="0"
                    max="5"
                    step="0.1"
                    value={evaluationForm.compliance_score}
                    onChange={handleEvaluationChange}
                    required
                  />
                </label>
              </div>

              <label className="supplier-notes">
                Observaciones
                <textarea
                  name="observations"
                  value={evaluationForm.observations}
                  onChange={handleEvaluationChange}
                  placeholder="Aspectos positivos, oportunidades de mejora o novedades"
                  rows="3"
                />
              </label>

              <button
                type="submit"
                className="btn-supplier-save"
                disabled={savingEvaluation}
              >
                {savingEvaluation
                  ? "Guardando..."
                  : "Guardar evaluación"}
              </button>
            </form>

            <div className="supplier-modal-list">
              <h3>Historial de evaluaciones</h3>

              {loadingEvaluations ? (
                <p className="supplier-empty">
                  Cargando evaluaciones...
                </p>
              ) : evaluations.length === 0 ? (
                <p className="supplier-empty">
                  No existen evaluaciones para este proveedor.
                </p>
              ) : (
                evaluations.map((evaluation) => (
                  <article
                    className="supplier-evaluation-item"
                    key={evaluation.id}
                  >
                    <div>
                      <strong>
                        {getMonthName(evaluation.month)}{" "}
                        {evaluation.year}
                      </strong>

                      <span>
                        Calidad: {evaluation.quality_score} · Entrega:{" "}
                        {evaluation.delivery_score} · Cumplimiento:{" "}
                        {evaluation.compliance_score}
                      </span>

                      {evaluation.observations && (
                        <small>{evaluation.observations}</small>
                      )}
                    </div>

                    <span className="supplier-score good">
                      {Number(evaluation.overall_score).toFixed(2)} / 5
                    </span>
                  </article>
                ))
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default CompanySuppliers;